/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module SharedModule
 */

import {
    AfterViewInit,
    Component,
    ContentChild,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnDestroy,
    Output,
    ViewChild,
} from '@angular/core';
import { L10nService, VIPModule } from '@vmw/ngx-vip';
import { CommonModule } from '@angular/common';
import { ClrFormsModule } from '@clr/angular';
import { A11yModule } from '@angular/cdk/a11y';
import { AviDropdownOptionsComponent } from '../avi-dropdown-options';
import { AviInputContainerComponent } from '../../avi-input-container';
import './avi-dropdown-menu.component.less';
import * as l10n from './avi-dropdown-menu.l10n';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;

/**
 * Component for the AviDropdown menu. Contains an optional search input and transcluded dropdown
 * options.
 * @author alextsg
 */
@Component({
    selector: 'avi-dropdown-menu',
    templateUrl: './avi-dropdown-menu.component.html',
    standalone: true,
    imports: [
        CommonModule,
        AviInputContainerComponent,
        ClrFormsModule,
        A11yModule,
        VIPModule,
    ],
})
export class AviDropdownMenuComponent implements AfterViewInit, OnDestroy {
    /**
     * If true, hides the search input.
     */
    @Input()
    public hideSearch = false;

    /**
     * Search input model.
     */
    @Input()
    public searchTerm = '';

    /**
     * Called when user types in the search input.
     */
    @Output()
    public onSearch = new EventEmitter();

    /**
     * Called when user types in the search input.
     */
    @Output()
    public onDestroy = new EventEmitter();

    /**
     * Search input field.
     */
    @ViewChild('searchInput')
    private searchInputRef: ElementRef;

    @ContentChild(AviDropdownOptionsComponent)
    private aviDropdownOptions: AviDropdownOptionsComponent;

    /**
     * Get keys from source bundles for template usage
     */
    public readonly l10nKeys = l10nKeys;

    public constructor(l10nService: L10nService) {
        l10nService.registerSourceBundles(dictionary);
    }

    /**
     * Listener for keydown events. Close the menu when Escape or Tab keys have been pressed.
     */
    @HostListener('keydown', ['$event'])
    private onKeydown(event: KeyboardEvent): void {
        switch (event.key) {
            case 'Escape':
            case 'Tab':
                event.stopPropagation();
                this.onDestroy.emit();
                break;
        }
    }

    /**
     * @override
     * If hideSearch is not true, place initial focus on the search input.
     */
    public ngAfterViewInit(): void {
        setTimeout(() => {
            if (!this.hideSearch) {
                this.focusSearch();
            }
        });
    }

    /**
     * Focus search input. Also called by parent AviDropdown component.
     */
    public focusSearch(): void {
        this.searchInputRef.nativeElement.focus();
    }

    /**
     * Handler called when the user types in the search input.
     */
    public handleSearch(event: Event): void {
        const { target } = event;
        const { value: searchTerm } = target as HTMLInputElement;

        this.onSearch.emit(searchTerm);
    }

    /**
     * Stop propagation for clr-modal to prevent auto-refocus. Clarity does auto-refocusing back
     * to the modal when focusing pop-up input that doesn't belong to the modal, so the search input
     * can't be focused under a dialog.
     * This mechanism needs to be coupled with stopping propagation for focus-out.
     */
    public handleSearchFocusIn(event: Event): void {
        event.stopPropagation();
    }

    /**
     * Stop propagation for clr-modal to prevent auto-refocus. Clarity does auto-refocusing back
     * to the modal when focusing pop-up input that doesn't belong to the modal, so the search input
     * can't be focused under a dialog.
     * This mechanism needs to be coupled with stopping propagation for focus-in.
     */
    public handleSearchFocusOut(event: Event): void {
        event.stopPropagation();
    }

    /**
     * Handle keydown on search input field. Used to set focus on the dropdown options when
     * ArrowDown key is hit.
     */
    public handleSearchKeydown(event: KeyboardEvent): void {
        switch (event.key) {
            case 'ArrowDown':
                this.aviDropdownOptions.setActive();
                break;
        }
    }

    /**
     * @override
     */
    public ngOnDestroy(): void {
        this.onDestroy.emit();
    }
}
